import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Building2, FileText, Landmark, Languages, ReceiptText, Search, Settings, WalletCards } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { SUPPORTED_LOCALES, useLocale } from '../contexts/LocaleContext';

const helpContent = {
  en: {
    title: 'ServiceFlow Help', subtitle: 'Plain-language guidance from first setup through payment reconciliation.',
    search: 'Search help topics...', noResults: 'No help topic matches your search.', open: 'Open',
    language: 'Help language', settingsLink: 'Open Settings',
    sections: [
      { id: 'start', icon: Building2, title: '1. Set up your company', summary: 'Choose the correct edition before creating documents.', steps: [
        'In Settings, choose Canada, United States, or Germany / Europe as the business location.',
        'Choose the application language and document language separately.',
        'Confirm base currency, timezone, company address, payment terms, and document prefixes.',
        'Changing these defaults later does not rewrite historical documents.'
      ]},
      { id: 'tax', icon: ReceiptText, title: '2. Configure taxes', summary: 'Use the current tax controls for calculations and the dated list for rate history.', steps: [
        'Enable only taxes for which the company is registered and enter the registration number.',
        'The current rates are used on future quotes, estimates, and invoices. Existing documents keep their frozen tax totals.',
        'When a rate changes, update the current calculation rate on its effective date and add a dated replacement to the tax-rate history.',
        'For a German business supplying ordinary B2B services to a US business, select the special no-German-VAT treatment on the quote or invoice. Confirm exceptional services with a tax adviser.'
      ]},
      { id: 'customers', icon: Building2, title: '3. Add clients and projects', summary: 'A client can have multiple projects, contacts, PO numbers, and tickets.', steps: [
        'Create the client first, including the billing address and email.',
        'Create a project under that client and set optional default PO and ticket numbers.',
        'Quotes, estimates, invoices, payments, expenses, and credit notes remain grouped under the project.'
      ]},
      { id: 'documents', icon: FileText, title: '4. Quote and invoice', summary: 'Use stable position numbers for clear progress billing.', steps: [
        'Create a quote or estimate with one position per billable service, week, material, or travel item.',
        'Choose the tax treatment before saving. Position numbers remain stable after invoicing.',
        'Create progress invoices by selecting only the positions and quantities being billed now.',
        'Review the PDF, tax note, client PO, due date, and totals before sending.'
      ]},
      { id: 'payments', icon: WalletCards, title: '5. Record payments', summary: 'Record the actual payment date and allocate one payment across invoices if needed.', steps: [
        'Open the project or invoice and choose Record Payment.',
        'Enter the date the money was received, amount, method, and reference.',
        'The payment date remains editable. ServiceFlow updates invoice balances and payment history.'
      ]},
      { id: 'banking', icon: Landmark, title: '6. Connect banking and reconcile', summary: 'Read-only bank data helps match deposits; ServiceFlow never moves money.', steps: [
        'Connect the business bank through Banking and select which accounts participate in matching.',
        'Credit cards can remain visible on the dashboard while being excluded from deposit matching.',
        'Sync transactions, review suggestions, and explicitly confirm before a payment is recorded.',
        'If a provider balance is unavailable or unreliable, ServiceFlow omits it instead of estimating it.'
      ]},
      { id: 'language', icon: Languages, title: '7. Language and regional demos', summary: 'Sign-up sets the first language; Settings always remains available for demonstrations.', steps: [
        'Application language controls navigation and help text.',
        'Document language is stored separately for customer-facing documents.',
        'For demo accounts, switch region and language in Settings. Review taxes and currency before creating demo documents.'
      ]},
      { id: 'troubleshooting', icon: Settings, title: '8. Quick troubleshooting', summary: 'A few safe checks solve most display or connection issues.', steps: [
        'Hard-refresh the browser after a new ServiceFlow release.',
        'If banking is stale, open Banking and run Sync transactions.',
        'If a document will not save, check required descriptions, dates, and remaining progress-billing quantities.',
        'Do not delete historical records to correct settings; update future defaults or create a credit note.'
      ]},
    ],
  },
  de: {
    title: 'ServiceFlow-Hilfe', subtitle: 'Verständliche Anleitungen von der Einrichtung bis zum Bankabgleich.',
    search: 'Hilfethemen durchsuchen...', noResults: 'Kein passendes Hilfethema gefunden.', open: 'Öffnen',
    language: 'Hilfesprache', settingsLink: 'Einstellungen öffnen',
    sections: [
      { id: 'start', icon: Building2, title: '1. Unternehmen einrichten', summary: 'Wähle die richtige Edition, bevor Dokumente erstellt werden.', steps: [
        'Wähle in den Einstellungen Kanada, USA oder Deutschland / Europa als Unternehmensstandort.',
        'Anwendungssprache und Dokumentsprache können getrennt eingestellt werden.',
        'Prüfe Basiswährung, Zeitzone, Firmenanschrift, Zahlungsziel und Dokumentpräfixe.',
        'Spätere Änderungen dieser Vorgaben verändern keine historischen Dokumente.'
      ]},
      { id: 'tax', icon: ReceiptText, title: '2. Steuern einrichten', summary: 'Die aktuellen Steuerfelder steuern die Berechnung; die datierte Liste dokumentiert den Ratenverlauf.', steps: [
        'Aktiviere nur Steuern, für die das Unternehmen registriert ist, und hinterlege die Steuernummer.',
        'Aktuelle Sätze gelten für zukünftige Angebote und Rechnungen. Bestehende Dokumente behalten ihre gespeicherten Steuerbeträge.',
        'Bei einer Steuersatzänderung wird der aktuelle Berechnungssatz zum Stichtag angepasst und eine datierte Folgeversion im Steuerverlauf angelegt.',
        'Bei normalen B2B-Leistungen eines deutschen Unternehmens an ein US-Unternehmen kann die besondere Behandlung ohne deutsche Umsatzsteuer gewählt werden. Sonderfälle bitte steuerlich prüfen lassen.'
      ]},
      { id: 'customers', icon: Building2, title: '3. Kunden und Projekte anlegen', summary: 'Ein Kunde kann mehrere Projekte, Ansprechpartner, Bestellnummern und Tickets haben.', steps: [
        'Lege zuerst den Kunden mit Rechnungsanschrift und E-Mail-Adresse an.',
        'Erstelle darunter ein Projekt und hinterlege bei Bedarf Standard-PO- und Ticketnummern.',
        'Angebote, Rechnungen, Zahlungen, Ausgaben und Gutschriften bleiben dem Projekt zugeordnet.'
      ]},
      { id: 'documents', icon: FileText, title: '4. Angebote und Rechnungen', summary: 'Stabile Positionsnummern ermöglichen eine klare Abschlags- und Teilabrechnung.', steps: [
        'Erstelle je Leistung, Woche, Material oder Fahrt eine eigene Position.',
        'Wähle vor dem Speichern die steuerliche Behandlung. Berechnete Positionsnummern bleiben stabil.',
        'Für Teilrechnungen werden nur die jetzt abzurechnenden Positionen und Mengen ausgewählt.',
        'Prüfe vor dem Versand PDF, Steuerhinweis, Kunden-PO, Fälligkeit und Summen.'
      ]},
      { id: 'payments', icon: WalletCards, title: '5. Zahlungen erfassen', summary: 'Erfasse das tatsächliche Zahlungsdatum und verteile Sammelzahlungen bei Bedarf.', steps: [
        'Öffne Projekt oder Rechnung und wähle Zahlung erfassen.',
        'Trage Zahlungseingangsdatum, Betrag, Zahlungsart und Referenz ein.',
        'Das Zahlungsdatum bleibt bearbeitbar; Rechnungsrest und Zahlungshistorie werden aktualisiert.'
      ]},
      { id: 'banking', icon: Landmark, title: '6. Bank verbinden und abgleichen', summary: 'Schreibgeschützte Bankdaten helfen beim Zuordnen; ServiceFlow bewegt kein Geld.', steps: [
        'Verbinde das Geschäftskonto im Bankabgleich und wähle die Konten für die Zahlungszuordnung aus.',
        'Kreditkarten können im Dashboard sichtbar bleiben und gleichzeitig vom Einzahlungsabgleich ausgeschlossen werden.',
        'Synchronisiere Umsätze, prüfe Vorschläge und bestätige ausdrücklich, bevor eine Zahlung gebucht wird.',
        'Nicht verfügbare oder unzuverlässige Salden werden nicht geschätzt, sondern ausgeblendet.'
      ]},
      { id: 'language', icon: Languages, title: '7. Sprache und regionale Demos', summary: 'Die Registrierung setzt die erste Sprache; in den Einstellungen bleibt alles umschaltbar.', steps: [
        'Die Anwendungssprache steuert Navigation und Hilfetexte.',
        'Die Sprache für Kundendokumente wird separat gespeichert.',
        'Für Demo-Konten können Region und Sprache in den Einstellungen gewechselt werden. Vor Demo-Dokumenten Steuern und Währung prüfen.'
      ]},
      { id: 'troubleshooting', icon: Settings, title: '8. Schnelle Fehlerbehebung', summary: 'Einige sichere Prüfungen lösen die meisten Anzeige- und Verbindungsprobleme.', steps: [
        'Nach einem ServiceFlow-Update den Browser vollständig neu laden.',
        'Bei veralteten Bankdaten den Bankabgleich öffnen und Umsätze synchronisieren.',
        'Wenn ein Dokument nicht speichert, Pflichtbeschreibungen, Datumsfelder und Restmengen prüfen.',
        'Historische Daten nicht löschen, um Einstellungen zu korrigieren; zukünftige Vorgaben ändern oder eine Gutschrift erstellen.'
      ]},
    ],
  },
};

export default function HelpPage() {
  const { language, locale, setLocale } = useLocale();
  const [query, setQuery] = useState('');
  const text = helpContent[language];
  const sections = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return text.sections;
    return text.sections.filter((section) =>
      [section.title, section.summary, ...section.steps].join(' ').toLowerCase().includes(needle)
    );
  }, [query, text]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{text.title}</h1>
          </div>
          <p className="text-sm text-slate-500 mt-2">{text.subtitle}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="text-xs font-medium text-slate-600">
            {text.language}
            <select value={locale} onChange={(event) => setLocale(event.target.value)} className="mt-1 flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              {SUPPORTED_LOCALES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <Button asChild variant="outline" className="self-end"><Link to="/settings">{text.settingsLink}</Link></Button>
          <Button asChild variant="outline" className="self-end"><Link to="/security">Security / Datenschutz</Link></Button>
          <Button asChild variant="outline" className="self-end"><Link to="/legal">Legal / Compliance</Link></Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
        <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={text.search} className="pl-9" data-testid="help-search" />
      </div>

      {sections.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">{text.noResults}</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <Card key={section.id} id={section.id} className="border border-slate-200 shadow-sm scroll-mt-6">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                    <div><CardTitle className="text-lg text-slate-900">{section.title}</CardTitle><p className="text-sm text-slate-500 mt-1">{section.summary}</p></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2 list-decimal pl-5 text-sm text-slate-700">
                    {section.steps.map((step) => <li key={step} className="pl-1">{step}</li>)}
                  </ol>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
